import { FC, useCallback, useContext, useMemo, useState } from "react";
import { Table, Button, TableColumnsType, Popconfirm, message, Popover, Row, Col, Tag } from "antd";
import { DrawRequestApprovalType, DrawRequestResp } from "../../../../types/draw-request";
import { convertWithCommas } from "../../../../helpers/convertWithCommas";
import { DataContext } from "../../../../contexts/DataContext";
import moment from "moment";
import { ApproverResponse } from "../../../../types/approvers";
import { AuthContext } from "../../../../contexts/AuthContext";
import { PaymentMethod } from "../../../../types/payment-method";
import { drawRequestsService } from "../../../../services/draw-request";
import { CheckOutlined, CloseOutlined, CopyOutlined, EditOutlined, StopOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router";
// import { useMsal } from "@azure/msal-react";
import { canBeApprovedOrDenied } from "../../helpers/canBeApprovedOrDenied";
import { canVoid } from "../../helpers/canVoid";

interface IDrawRequestsTable {
  data: DrawRequestResp[];
  setData: React.Dispatch<React.SetStateAction<DrawRequestResp[]>>;
  onAction: (type: DrawRequestApprovalType, drawRequest?: DrawRequestResp) => void;
  loading: boolean;
  hideApproveAll: boolean;
}

export const DrawRequestsTableNew: FC<IDrawRequestsTable> = ({ data, onAction, setData, loading, hideApproveAll }) => {
  // const { accounts: accountsMsal } = useMsal();

  // const accountName = accountsMsal && accountsMsal.length ? accountsMsal[0]?.name : "";

  const { accounts } = useContext(DataContext);
  const { isAccountant, isTreasury, userExtraInfo } = useContext(AuthContext);
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  // const renderAccountOptions = (options: Account[]) => [

  const columns: TableColumnsType<DrawRequestResp> = useMemo(
    () => [
      {
        dataIndex: "submitDate",
        title: "Submit Date",
        key: "submitDate",
        width: "10%",
        align: "center",
        render: (text: string) => (text ? moment(text).format("MM/DD/YY") : "none"),
        sorter: (a: any, b: any) => {
          try {
            return new Date(a.submitDate).getTime() - new Date(b.submitDate).getTime();
          } catch (error) {
            return -1;
          }
        }
      },
      {
        title: "Payment Due Date",
        dataIndex: "dueDate",
        key: "dueDate",
        width: "10%",
        align: "left",
        sorter: (a: any, b: any) => {
          try {
            return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
          } catch (error) {
            return -1;
          }
        },
        render: (text: string) => <strong>{text ? moment(text).format("MM/DD/YY") : "none"}</strong>
      },
      {
        dataIndex: "amount",
        title: "Amount",
        key: "amount",
        width: "15%",
        align: "center",
        render: (value: number) => "$" + convertWithCommas(value)
      },
      {
        dataIndex: "payee",
        title: "Vendor Name",
        key: "vendorName",
        width: "15%",
        align: "center"
      },
      {
        dataIndex: "accountID",
        title: "MFA JV Name (Number)",
        key: "accountID",
        width: "25%",
        align: "center",
        render: (value: number) => {
          const account = accounts?.find((acc) => acc.accountID === value);
          if (!account) return <p>-Temporary-</p>;
          return <p>{`${account?.accountName} (${account?.glAccountCode})`}</p>;
        }
      },

      {
        dataIndex: "approvalStatus",
        title: "Approval Status",
        key: "approvalStatus",
        width: "28%",
        align: "center",
        render: (value: string) => {
          if (value?.toLowerCase() === "completed") {
            return <Tag color="success">{`${value ? value : "none"}`}</Tag>;
          } else if (value?.toLowerCase() === "rejected") {
            return <Tag color="error">{`${value ? value : "none"}`}</Tag>;
          } else if (value?.toLowerCase() === "voided") {
            return <Tag color="purple">{`${value ? value : "none"}`}</Tag>;
          }
          return <Tag color="blue">{`${value ? value : "none"}`}</Tag>;
        }
        // (value ? value : "none")
      },
      {
        dataIndex: "id",
        // title: "Payment Request (PDF)",
        title: "Review Payment Request",
        key: "paymentRequest",
        width: "18%",
        align: "center",
        render: (value: number) => (
          <Button
            onClick={() => {
              navigate(`preview-draw-request/${value}`);
            }}
            type="primary"
          >
            Review
          </Button>
        )
      },
      {
        dataIndex: "initiator",
        title: "Initiator",
        key: "initiator",
        width: "14%",
        align: "left"
      },
      {
        dataIndex: "paymentMethod",
        title: "Payment Type",
        key: "paymentMethod",
        width: "10%",
        align: "left",
        render: (value: PaymentMethod) => value.name
      },
      {
        dataIndex: "controlNumber",
        title: "Control Number",
        key: "controlNumber",
        width: "12%",
        align: "left",
        render: (value: string, row: DrawRequestResp) => {
          if (!value || value == "0") return `${row.id}-0`;
          return value;
        }
      },

      {
        title: (
          <>
            <div>
              <p>Actions</p>

              {/* //TODO: approve all commented for now */}
              {/* {(isAccountant || isTreasury) && (
                <Popconfirm title="Are you sure you want to approve all transaction?" onConfirm={() => approveAllTransaction()}>
                  <Button style={{ padding: "5px" }} type="primary">
                    <CheckOutlined style={{ fontSize: "1.2rem" }} />
                    Approve all
                  </Button>
                </Popconfirm>
              )} */}
            </div>
          </>
        ),
        align: "center",
        dataIndex: "approvers",
        key: "actions",
        width: "18%",
        render: (value: ApproverResponse[], row: DrawRequestResp) => (
          <Row style={{ display: "flex", flexDirection: "row", justifyContent: "center" }}>
            {canBeApprovedOrDenied(row, isAccountant, isTreasury) && (
              // <Popover
              //   visible={canBeApprovedOrDenied(row) ? false : undefined}
              //   content="This transaction can not be approved or denied by you or can not bet modify yet"
              // >
              <>
                <Popconfirm
                  title="Are you sure you want to approve this transaction?"
                  onConfirm={() => {
                    approveDenyTransaction(row.id, true);
                  }}
                >
                  <Button style={{ padding: "5px", marginRight: 5 }} type="primary">
                    <CheckOutlined style={{ fontSize: "1.2rem" }} />
                  </Button>
                </Popconfirm>

                <Popconfirm
                  title="Are you sure you want to deny this transaction?"
                  onConfirm={() => {
                    approveDenyTransaction(row.id, false);
                  }}
                >
                  <Button style={{ padding: "5px", marginRight: 5 }} type="primary" danger>
                    <CloseOutlined style={{ fontSize: "1.2rem" }} />
                  </Button>
                </Popconfirm>
                {/* </Popover> */}
              </>
            )}
            {canVoid(row, userExtraInfo) && (
              <Popconfirm
                title="Are you sure you want to void this draw request?"
                onConfirm={() => {
                  voidDrawRequest(row.id);
                }}
              >
                <Button style={{ padding: "5px", marginRight: 5 }} type="primary" danger>
                  <StopOutlined style={{ fontSize: "1.2rem" }} />
                </Button>
              </Popconfirm>
            )}
            {canEdit(row) && (
              <Button style={{ padding: "5px", marginRight: 5 }} type="primary" onClick={() => editDrawRequest(row.id, row)}>
                <EditOutlined style={{ fontSize: "1.2rem" }} />
              </Button>
            )}

            {canCopy(row) && (
              <Popconfirm
                title="Are you sure you want to copy this draw request?"
                onConfirm={() => {
                  editDrawRequest(row.id, row, true);
                }}
              >
                <Button style={{ padding: "5px" }} type="primary">
                  <CopyOutlined style={{ fontSize: "1.2rem" }} />
                </Button>
              </Popconfirm>
            )}
          </Row>
        )
      },
      {
        dataIndex: "accountingApprovalStatus",
        title: "Admin Status",
        key: "status",
        width: "10%",
        align: "center",
        render: (value: string) => (
          <Tag
            color={value?.toLocaleLowerCase() === "approved" ? "success" : value?.toLocaleLowerCase() === "denied" ? "error" : "blue"}
          >{`${value ? value : "none"}`}</Tag>
        )
      }
    ],
    [data, hideApproveAll, accounts, loading]
  );

  // const cannotBeModify = (drawRequest: DrawRequestResp) => {
  //   return drawRequest.accountingApprovalStatus?.toLowerCase() === "approve" && drawRequest.approvalStatus?.toLowerCase() === "complete";
  // };

  const approveDenyTransaction = async (id: number | string, isApprove: boolean = true) => {
    setIsLoading(true);
    try {
      const drawRequest = await drawRequestsService.approveDenyTransaction(id, isApprove);

      setData((old) => [...old.map((dr) => (dr.id === id ? drawRequest : dr))]);

      message.success(`transaction ${isApprove ? "approved" : "denied"}`);
    } catch (error) {
      console.error(error);
      message.error(
        `Ups: something was wrong ${
          isApprove ? "approving" : "denying"
        } the transaction, please try again later or contact the support team`
      );
    }
    setIsLoading(false);
  };

  const canEdit = (drawRequest: DrawRequestResp) => {
    if (!drawRequest.isDraft) return false;

    return drawRequest.initiatorId === userExtraInfo?.id?.toString();
  };
  const canCopy = (drawRequest: DrawRequestResp) => {
    const approvalStatus = drawRequest?.approvalStatus?.toLowerCase();

    if (approvalStatus !== "voided" && approvalStatus !== "completed" && approvalStatus !== "rejected") return false;

    return drawRequest.initiatorId === userExtraInfo?.id?.toString();
  };

  // const approveAllTransaction = async () => {
  //   setIsLoading(true);
  //   try {
  //     const transactions = data?.filter((dr) => canBeApprovedOrDenied(dr));
  //     if (!transactions.length) message.info(`There are not transactions that can be approved`);
  //     else {
  //       await drawRequestsService.approveAllRequestsV2(transactions);
  //       message.success(`All transactions were approved`);
  //     }
  //   } catch (error) {
  //     console.error(error);
  //     message.error(`Ups: something was wrong approving all transactions, please try again later or contact the support team`);
  //   }
  //   setIsLoading(false);
  // };

  const voidDrawRequest = async (id: number) => {
    try {
      setIsLoading(true);
      await drawRequestsService.voidDrawRequest(id);
      setData((old) => [
        ...old.map((dr) => {
          if (dr.id === id) return { ...dr, approvalStatus: "Voided" };
          return dr;
        })
      ]);
      message.success(`Draw request voided`);
    } catch (error) {
      console.error(error);
      message.error(`Ups: something was wrong voiding the draw request, please try again later or contact the support team`);
    }
    setIsLoading(false);
  };
  // const deleteDrawRequest = async (id: number) => {
  //   try {
  //     setIsLoading(true);
  //     await drawRequestsService.deleteDrawRequest(id);
  //     setData((old) => [...old.filter((dr) => dr.id !== id)]);
  //     message.success(`Draw request rejected`);
  //   } catch (error) {
  //     console.error(error);
  //     message.error(`Ups: something was wrong rejecting the draw request, please try again later or contact the support team`);
  //   }
  //   setIsLoading(false);
  // };

  const editDrawRequest = (id: number | string, drawRequest: DrawRequestResp, isCopy?: boolean) => {
    if (!isCopy)
      navigate(`edit-draw-request/${id}`, {
        state: { drawRequest }
      });
    else
      navigate(`copy-draw-request/${id}`, {
        state: { drawRequest }
      });
  };

  return (
    // <Spin spinning={isLoading} tip="Working on it..." size="large">
    <Row>
      <Col span={24} style={{ paddingLeft: "30px", paddingRight: "30px" }}>
        <Table
          rowClassName={(record) => (record.isDraft ? "draft-row" : "")}
          rowKey="id"
          sticky
          dataSource={data || []}
          columns={columns}
          loading={loading || isLoading}
          pagination={false}
          bordered
          scroll={{ x: 1000 }}
        />
      </Col>
    </Row>
    // </Spin>
  );
};
